import { FormControl, FormControlProps, FormHelperText, InputLabel, TextField } from "@mui/material";
import { createElement, useContext, KeyboardEvent } from "react";
import { useActionData } from "react-router-dom";
import { FormContext } from "./Form";
import { toSnake } from "ts-case-convert";

interface FieldControlProps extends Omit<FormControlProps, "children"> {
  name: string;
  label?: string;
  helperText?: string;
  component?: React.ComponentType<FieldProps> | "input" | "select" | "textarea";
  inputProps?: FieldProps;
  children?: (inputProps: FieldProps) => React.ReactNode;
}

interface FieldProps {
  name?: string;
  label?: string;
  helperText?: string;
  error?: boolean;
  value?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  multiple?: boolean;
  checked?: boolean;
  labelId?: string;
  defaultValue?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  onChange?: (event: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  required?: boolean;
  type?: string;
}

const HelperText = ({
  hasError,
  errorMessages,
  helperText,
}: {
  hasError?: boolean;
  errorMessages?: string[];
  helperText?: string;
}) => {
  if (hasError) {
    return <>{errorMessages?.map((err) => <FormHelperText key={err}>{err}</FormHelperText>)}</>;
  }
  if (helperText) {
    return <FormHelperText>{helperText}</FormHelperText>;
  }
  return null;
};

export const FieldControl: React.FC<FieldControlProps> = ({
  name,
  label,
  helperText,
  component,
  inputProps,
  children,
  required,
  hiddenLabel,
  ...formControlProps
}) => {
  const actionData = useActionData();
  const errors = (actionData as { errors?: Record<string, string[]> })?.errors;
  const hasError = Boolean(errors?.[name]?.length);
  const { resource } = useContext(FormContext);

  const nameWithResource = `${resource}[${toSnake(name)}]`;
  const errorMessages = errors?.[name] ?? [];

  const fieldProps: FieldProps = {
    ...inputProps,
    name: nameWithResource,
    label,
    helperText: hasError ? errorMessages.join(", ") : helperText,
    error: hasError || inputProps?.error,
    labelId: `${name}-label`,
    defaultValue: inputProps?.defaultValue,
    onChange: inputProps?.onChange,
    required,
  };

  // Special case for TextField
  if (component === TextField) {
    return createElement(TextField, fieldProps);
  }

  // If custom component is provided
  if (component) {
    return (
      <FormControl error={hasError} {...formControlProps}>
        {label && !(hiddenLabel || inputProps?.type === "hidden") && (
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
        )}
        {createElement(component, fieldProps)}
        <HelperText hasError={hasError} errorMessages={errorMessages} helperText={helperText} />
      </FormControl>
    );
  }

  // If children render prop is provided
  return (
    <FormControl error={hasError} {...formControlProps}>
      {label && <InputLabel id={`${name}-label`}>{label}</InputLabel>}
      {children?.(fieldProps)}
      <HelperText hasError={hasError} errorMessages={errorMessages} helperText={helperText} />
    </FormControl>
  );
};
